/**
 * paper-1-specs — 지면 파이프라인 ① 규칙.
 *   품목표의 어휘(QuoteItem·QuoteCtx)와 규칙(열 명세 ColumnSpec · 덧붙임 줄 문구(품번·대체) ·
 *   돈 산수)을 정의한다. 실행 순서: ①규칙(이 파일) → ②채움(paper-2-fill) → ③그리기(paper-3-html).
 *
 * 여기 열 명세 한 줄이 웹 지면·고객 뷰·출력 xlsx 세 곳을 동시에 바꾼다("매핑의 정본").
 * 좌표 근거: HCLTECH 견적서 v3.xlsx 실측 (2026-08-27).
 */

/** 견적서 유형 — 유형마다 템플릿과 열 구성이 다르다(= 템플릿 교체).
 *  임대는 서버/파트 층을 가로질러 열이 다르다(컨디션·판매단가 → 기간·임대단가) — 그래서 4갈래. */
export type QuoteDocType = 'server' | 'part' | 'part-rental' | 'server-rental';

export type ColAlign = 'left' | 'center' | 'right';

/** 소스(매입처)의 종류 — 소스 팝오버가 행에 붙여준다. */
import { assert } from '../../lib/assert';
import { conditionGroup } from '@hcltech/shared/condition/condition-group';

export type SourceType = 'stock' | 'deal_rfq' | 'rfq_history';

/**
 * 컨디션 표시 — 고객 문서는 내부 표기(F/S·REF·Newbulk…)가 아니라 한글 3그룹
 * (신품·벌크·중고)으로 나간다. 정본은 shared 의 conditionGroup(2026-07 확정).
 * '미확인'은 고객에게 의미 없는 말이라 빈칸으로 둔다 — 채움이 틀 문구를 유지.
 */
export function conditionText(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const group = conditionGroup(raw);
  return group === '미확인' ? null : group;
}

/** 소스 종류별 표시 라벨 — SourceType 에 새 종류가 생기면 여기 한 칸이 비어 컴파일 에러. */
export const SOURCE_TYPE_LABELS: Record<SourceType, string> = {
  stock:       '재고',
  deal_rfq:    'RFQ·딜',
  rfq_history: 'RFQ',
};

/** 칸의 편집 성격. */
export type EditKind =
  | 'text'      // 자유 입력 (파트 검색)
  | 'number'    // 수치 입력 (수량 · 단가)
  | 'derived'   // 파생값, 입력 아님 (금액 = 수량 × 단가)
  | 'picked';   // 선택으로 채움 (구분 · 컨디션 — 레일/드롭다운, 지면 직접 타이핑 X)

/**
 * 매입 후보 하나 — 이 파트넘버를 어디서 얼마에 살 수 있나(재고 또는 RFQ). API(SourcesResponse)는 View 국경에서 이 모양으로.
 * 패널(레퍼런스)에서 [선택]하면 그대로 품목의 소스가 된다 — 그래서 저장에 필요한 원본(종류 셋·금액·통화)을 들고 있다.
 */
export interface CostCandidate {
  sourceType: SourceType;       // 저장은 셋을 구분한다(같은 딜 RFQ / 이력) — 화면 배지는 SOURCE_TYPE_LABELS
  sourceRefId: string;          // 재고 id / RFQ id — 지금 붙은 소스(item.sourceRefId)와 대조
  label: string;                // 재고 'New · 4개' · RFQ '08/21 · (주)서버월드'
  costAmount: number | null;    // 저장용 원본 금액(cost_basis_amount)
  costCurrency: string | null;  // 저장용 원본 통화(cost_basis_currency)
  costUnitKrw: number | null;   // 매입 단가(원 환산 — USD 는 krwPerUsd). 원본에서 계산한 값 — ctx 를 갈 때 다시 만든다
}

/** 후보 로드 전 — 빈 표(Null Object, NO_TITLE 과 같은 자리). */
export const NO_COST_CANDIDATES: ReadonlyMap<string, CostCandidate[]> = new Map();

/** 견적 한 판의 바깥 사정 — 행 밖의 값. 순수 계산·채움이 받는 환경 봉지(파라미터 3부류의 '판'). */
export interface QuoteCtx {
  /** 실시가 환율 — 달러당 원 (1500 = 1달러에 1,500원). 발송 시점 라이브,
   *  동결하지 않는다 (설계 §5, Q2 결정 2026-08-27). */
  krwPerUsd: number;
  /** 파트넘버 → 매입 후보들. 화면이 뜬 뒤 백그라운드로 한 번 채워진다 — 그 전엔 NO_COST_CANDIDATES. */
  costCandidates: ReadonlyMap<string, CostCandidate[]>;
}

/**
 * 표제 — 문서 머리에 적는 이 견적 건의 신원: 누구에게(고객사·담당) · 무슨 건(건명) ·
 * 언제(발행·유효) · 몇 번(견적번호). 어느 칸에 앉는지는 지도(header.valueCells)가 정한다.
 * null = 그 데이터 없음 — 채움은 그 칸을 건너뛰어 틀 문구를 유지한다.
 */
export interface QuoteTitle {
  dealName: string | null;      // 건명
  quoteNo: string | null;       // 견적번호
  customerName: string | null;  // 고객사(거래처)명
  issuedAt: string | null;      // 발행일(ISO 날짜 문구)
  validUntil: string | null;    // 유효일(ISO 날짜 문구)
  contactName: string | null; contactPhone: string | null; contactEmail: string | null;  // 고객 측 담당
  assigneeName: string | null; assigneeEmail: string | null;                             // 우리 측 담당
}

/** 표제 없음 — 모든 필드 null: 헤더 값 칸 전부 틀 문구 유지. */
export const NO_TITLE: QuoteTitle = {
  dealName: null, quoteNo: null, customerName: null, issuedAt: null, validUntil: null,
  contactName: null, contactPhone: null, contactEmail: null, assigneeName: null, assigneeEmail: null,
};

/**
 * 지면 행 모델 — 세 투영이 공통으로 읽는 값 모양.
 * quote_item(마이그 057) + 자립 컬럼(마이그 301) + 카탈로그 조인(견적 편집 데이터(QuoteEditor)).
 * (모달 개편안의 QuoteItem 통합 분석을 이 타입으로 승격 — 어댑터가 api → QuoteItem 변환)
 */
export interface QuoteItem {
  id: number | null;
  category: string | null;        // 057 category         — 구분(CPU/MEMORY/…)
  optionPart: string | null;      // 057 option_part      — 실제 공급 파트넘버 (= 카탈로그 매칭 키)
  requestedPart: string | null;   // 301 requested_part   — 원 요청 파트(대체 표기용, 안 지워짐)
  condition: string | null;       // 301 requested_condition — New/RF … (견적 자립)
  // ── 카탈로그 조인 (견적 편집 데이터(QuoteEditor)가 option_part 로 해석 — quote_item 필드 아님) ──
  sparePart: string | null;       // catalog.spare_part    — 보조행 스페어 P/N
  assemblyPart: string | null;    // catalog.assembly_part — (서버 CTO 어셈블리용, 표시 선택)
  quantity: number | null;        // 057 quantity
  // ── 단가: 원화 기준. usd 소스면 usd×실시가fx, krw 네이티브면 그대로 (Q2) ──
  unitPriceUsd: number | null;    // 057 unit_price_usd    — USD 소스면 이게 정본
  unitPriceKrw: number | null;    // 057 unit_price_krw    — KRW 네이티브면 이게 정본
  isBasePart: boolean;            // 301 is_base_part      — 섀시 기본구성(여백 '기본' 태그)
  isUnregistered: boolean;        // 301 is_unregistered   — 임시등록 파트
  parentId: number | null;        // 301 parent_quote_item_id — 서버-부품 트리 (지면에서 추가한 부품이 쓰는 링크)
  dealItemId: number | null;      // deal_item_id — 이 행이 옮겨 온 딜 구성 항목 (서버 행이면 부품들의 묶음 열쇠)
  parentDealItemId: number | null;// parent_deal_item_id — 부품 → 서버의 deal_item (딜에서 만든 견적이 쓰는 링크)
  // ── 서버 견적 그룹핑용(마이그 131·자립) ──
  lineType: 'server' | null;      // 'server' = 서버 헤더 행 (그 외 값은 존재하지 않는다)
  costUsd: number | null;         // 087 cost_basis_usd 또는 105 cost_basis_amount(USD) — 매입원가 USD(단가)
  costKrw: number | null;         // 105 cost_basis_amount(KRW) — 매입원가 KRW
  serverQuantity: number | null;  // 131 server_quantity — 서버 대수
  unitPriceOverride: number | null; // 131 unit_price_override — 서버 1대가(NULL=부품합산)
  description: string | null;      // 표시명·스펙 파생 소스
  // ── 소스(매입처) 부착 (마이그 105) — 소스 팝오버로 채움 ──
  sourceType: SourceType | null;  // 105 source_type — 경계(toQuoteItem)에서 도장
  sourceRefId: string | null;     // 105 source_ref_id — 재고/RFQ 레코드 id(팝오버 현재 하이라이트 키)
  sourceName: string | null;      // 부착된 소스의 이름 — RFQ 면 공급사명(재고는 창고가 하나라 안 쓴다)
  sourceCondition: string | null; // 부착된 재고의 컨디션(inventory_stock.condition) — RFQ 면 null
  // ── 임대(마이그 178) — 임대 견적서 지면 전용. 판매 견적은 null(임대 라인 아님) ──
  rentalTermMonths: number | null;      // 178 rental_term_months — 임대 기간(개월). 적용 임대단가는 unitPriceKrw 에 박제
  rentalContractCostKrw: number | null; // 178 rental_contract_cost_krw — 라인 임대총원가(우리 뷰 임대 마진)
}

/**
 * 소스 부착 상태 라벨(우리 줄 전용). 오직 "소스를 골랐는가"로만 갈린다 — 원가만 손으로 넣은 행은 미선택
 * (원가 숫자는 옆 매입 칸에 이미 보인다). 재고 → '🔗 재고 · 컨디션'(창고가 하나라 이름 없음) · RFQ → '🔗 RFQ · 공급사명'.
 */
export function sourceLabel(item: QuoteItem, ctx: QuoteCtx): { text: string; isLinked: boolean } {
  const tail = costCandidatesTail(costCandidatesOf(item, ctx));
  if (item.sourceType == null) return { text: `＋ 소스 연결${tail}`, isLinked: false };
  const detail = item.sourceType === 'stock' ? item.sourceCondition : item.sourceName;
  return { text: `🔗 ${SOURCE_TYPE_LABELS[item.sourceType]}${detail ? ` · ${detail}` : ''}${tail}`, isLinked: true };
}

/** 소스 칸 꼬리 — '  ·  후보 N · 최저 매입 ₩X'. 후보가 없으면 빈 문구(로드 전도 같음). */
function costCandidatesTail(candidates: CostCandidate[]): string {
  if (candidates.length === 0) return '';
  const lowest = lowestCostUnitKrw(candidates);
  const lowestText = lowest == null ? '' : ` · 최저 매입 ₩${lowest.toLocaleString('ko-KR')}`;
  return `   ·   후보 ${candidates.length}${lowestText}`;
}

/** 이 품목 파트넘버의 매입 후보들 — 없으면 []. */
export function costCandidatesOf(item: QuoteItem, ctx: QuoteCtx): CostCandidate[] {
  if (item.optionPart == null) return [];
  return ctx.costCandidates.get(item.optionPart) ?? [];
}

/** 후보 중 가장 싼 매입 단가 — 매입가 없는 후보는 뺀다. */
export function lowestCostUnitKrw(candidates: CostCandidate[]): number | null {
  const costs = candidates.map((candidate) => candidate.costUnitKrw).filter((cost): cost is number => cost != null);
  return costs.length === 0 ? null : Math.min(...costs);
}

/** 이 판매 단가·이 매입 단가면 마진율 몇 % — (판매 − 매입) ÷ 판매. 판매가 없거나 0 이면 계산 불가. */
export function marginPercentAt(sellUnit: number | null, costUnit: number | null): number | null {   // 둘 다 원 단위
  if (sellUnit == null || costUnit == null || sellUnit === 0) return null;
  return Math.round(((sellUnit - costUnit) / sellUnit) * 100);
}

/** 이 매입 단가로 목표 마진율을 내려면 판매 단가 얼마 — 매입 ÷ (1 − p), 100원 단위 올림. */
export function sellUnitKrwAtMargin(costUnit: number, targetPercent: number): number {   // 원 단위
  assert(targetPercent >= 0 && targetPercent < 100, `목표 마진율은 0 이상 100 미만: ${targetPercent}`);
  return Math.ceil(costUnit / (1 - targetPercent / 100) / 100) * 100;
}

/** 판매 단가(원). USD 소스면 실시가 환율로 환산(동결 X), 아니면 KRW 네이티브. */
export function sellUnitKrw(item: QuoteItem, ctx: QuoteCtx): number | null {
  if (item.unitPriceUsd != null) return Math.round(item.unitPriceUsd * ctx.krwPerUsd);
  return item.unitPriceKrw ?? null;
}

/** 금액 = 판매단가 × 수량. xlsx 수식(=AH*AJ)과 원 단위로 일치 — 골든 테스트가 검증. */
export function sellAmountKrw(item: QuoteItem, ctx: QuoteCtx): number | null {
  const unitPrice = sellUnitKrw(item, ctx);
  return unitPrice == null || item.quantity == null ? null : unitPrice * item.quantity;
}

/** 매입원가(단가, 원). KRW 네이티브 우선, 아니면 USD×실시가fx. */
export function costUnitKrw(item: QuoteItem, ctx: QuoteCtx): number | null {
  if (item.costKrw != null) return item.costKrw;
  if (item.costUsd != null) return Math.round(item.costUsd * ctx.krwPerUsd);
  return null;
}
/** 매입금액 합계(원) = 매입단가 × 수량. sell 가족(sellUnitKrw·sellAmountKrw)과 대칭. */
export function costAmountKrw(item: QuoteItem, ctx: QuoteCtx): number | null {
  const costUnit = costUnitKrw(item, ctx);
  return costUnit == null || item.quantity == null ? null : costUnit * item.quantity;
}
/** 마진(총, 원) = 판매금액 − 매입금액 합계. */
export function marginKrw(item: QuoteItem, ctx: QuoteCtx): number | null {
  const sellAmount = sellAmountKrw(item, ctx); const costUnit = costUnitKrw(item, ctx);
  if (sellAmount == null || costUnit == null || item.quantity == null) return null;
  return sellAmount - costUnit * item.quantity;
}
/** 마진율(%) = 마진 / 판매금액. */
export function marginPercent(item: QuoteItem, ctx: QuoteCtx): number | null {
  const sellAmount = sellAmountKrw(item, ctx); const margin = marginKrw(item, ctx);
  if (sellAmount == null || margin == null || sellAmount === 0) return null;
  return Math.round((margin / sellAmount) * 100);
}

/* ── 임대 값 — 임대 견적서 지면의 3열. 판매 사슬과 맥락이 다르다(환산 없음·단가는 이미 박제). ── */

/** 임대 단가 — 적용 임대단가(원 네이티브). 저장 시 백엔드가 시트 R/S열 결과를 unit_price_krw 에 넣는다. */
export function getRentalUnitKrw(item: QuoteItem): number | null {
  return item.unitPriceKrw;
}

/** 월 임대 금액 = 임대단가 × 수량 (엑셀 AI열 · 2단 수식 1단). 재료 하나라도 없으면 null. */
export function getRentalMonthlyKrw(item: QuoteItem): number | null {
  const unit = getRentalUnitKrw(item);
  if (unit == null || item.quantity == null) return null;
  return unit * item.quantity;
}

/** 금액 합계 = 월 임대금액 × 기간(개월) (엑셀 AM열 · 2단 수식 2단). 계약 총액. */
export function getRentalTotalKrw(item: QuoteItem): number | null {
  const monthly = getRentalMonthlyKrw(item);
  if (monthly == null || item.rentalTermMonths == null) return null;
  return monthly * item.rentalTermMonths;
}

/** 대체 표기 여부 — 요청 ≠ 공급이면 대체행(↳)을 지면에 싣는다 (v3 규칙, 나간 7건 방지). */
export function isSubstituted(item: QuoteItem): boolean {
  return !!item.requestedPart && item.requestedPart !== item.optionPart;
}

/**
 * 열 명세 — 품목표 열 하나의 설계도(데이터가 아니라 규칙).
 * "무엇을 · 어느 엑셀 열 범위에 · 어떤 형식으로 · 어떻게 편집하나"를 한 줄에 묶는다.
 * 행 번호는 런타임 결정(서버별 품목표 시작행), 열 범위만 고정. xlsx 좌표 = 지면 그리드 좌표.
 */
export interface ColumnSpec {
  id: string;                          // 안정 키 ('qty')
  title: string;                       // BOM 헤더 라벨 ('수량')
  xlsxCol: readonly [string, string];  // 병합 열 범위 ['AH','AI']
  align: ColAlign;
  numFmt?: string;                     // 엑셀 표시서식 '#,##0' — 세 투영이 공유
  edit: EditKind;
  field?: keyof QuoteItem;              // 직접 필드 매핑(표시)
  editField?: keyof QuoteItem;          // 편집 대상 필드(표시와 다를 때 — 단가는 unitPriceKrw 로 씀)
  value?: (item: QuoteItem, ctx: QuoteCtx, index: number, isCustomerView: boolean) => number | string | null; // 파생(있으면 field 대신). isCustomerView 는 컨디션처럼 뷰마다 표기가 다른 칸만 본다

}

// ── 서버구매 견적서 품목표 열 명세 (v3 '견적서' r30 헤더 · r31~ 값행 실측) ────────────
export const SERVER_COLUMN_SPECS: readonly ColumnSpec[] = [
  { id: 'category',  title: '구분',                xlsxCol: ['D',  'H'],  align: 'left',   edit: 'picked', field: 'category'    },
  { id: 'part',      title: '구성 파트 / 파트넘버', xlsxCol: ['I',  'AB'], align: 'left',   edit: 'text', editField: 'description', value: (item) => item.description ?? item.optionPart },   // 파트명 칸 = 설명 편집. 파트넘버는 소스 선택·품목 추가로만 바뀐다
  { id: 'condition', title: '컨디션',              xlsxCol: ['AC', 'AF'], align: 'center', edit: 'picked', field: 'condition', value: (item, _ctx, _index, isCustomerView) => isCustomerView ? conditionText(item.condition) : item.condition },   // 우리 뷰: 고른 원형 코드(New/RF/REF/F/S) · 고객 뷰: 한글 3그룹

  { id: 'qty',       title: '수량',                xlsxCol: ['AH', 'AI'], align: 'right',  edit: 'number', field: 'quantity'    },
  { id: 'unitPrice', title: '판매단가',            xlsxCol: ['AJ', 'AM'], align: 'right', numFmt: '#,##0', edit: 'number', editField: 'unitPriceKrw', value: sellUnitKrw },
  { id: 'amount',    title: '판매금액 합계',        xlsxCol: ['AN', 'AP'], align: 'right', numFmt: '#,##0', edit: 'derived', value: sellAmountKrw },
] as const;

// ── 파트구매 견적서 품목표 열 명세 (파트 v3 '견적서' r20 헤더 실측 · 선두 No. 열) ────
export const PART_COLUMN_SPECS: readonly ColumnSpec[] = [
  { id: 'no',        title: 'No',                  xlsxCol: ['C',  'C'],  align: 'left',   edit: 'derived', value: (_item, _ctx, index) => String(index + 1).padStart(2, '0') },
  { id: 'category',  title: '구분',                xlsxCol: ['D',  'H'],  align: 'left',   edit: 'picked', field: 'category'    },
  { id: 'part',      title: '구성 파트 / 파트넘버', xlsxCol: ['I',  'AB'], align: 'left',   edit: 'text', editField: 'description', value: (item) => item.description ?? item.optionPart },   // 파트명 칸 = 설명 편집. 파트넘버는 소스 선택·품목 추가로만 바뀐다
  { id: 'condition', title: '컨디션',              xlsxCol: ['AC', 'AF'], align: 'center', edit: 'picked', field: 'condition', value: (item, _ctx, _index, isCustomerView) => isCustomerView ? conditionText(item.condition) : item.condition },   // 우리 뷰: 고른 원형 코드(New/RF/REF/F/S) · 고객 뷰: 한글 3그룹

  { id: 'qty',       title: '수량',                xlsxCol: ['AH', 'AI'], align: 'right',  edit: 'number', field: 'quantity'    },
  { id: 'unitPrice', title: '판매단가',            xlsxCol: ['AJ', 'AM'], align: 'right', numFmt: '#,##0', edit: 'number', editField: 'unitPriceKrw', value: sellUnitKrw },
  { id: 'amount',    title: '판매금액 합계',        xlsxCol: ['AN', 'AP'], align: 'right', numFmt: '#,##0', edit: 'derived', value: sellAmountKrw },
] as const;

/**
 * 품목 블록의 덧붙임 줄 문구 2종 — 파트·서버 채움이 같은 함수를 쓰는 정본.
 * 셀 안 줄바꿈 금지(템플릿 3금지) 때문에 긴 텍스트가 격자를 탈출하는 통로다.
 * null = 줄을 만들지 않는다. 어느 행·어느 서식으로 앉는지는 지도(paper-2)가 정한다.
 */

/** 품번 줄 — 품목 기본형의 둘째 줄(값행 + 품번 줄). 고객에게 실린다.
 *  값 예시: '옵션 P-100  ·  스페어 S-1' · 파트 미선택이면 null */
export function partNumberLineText(item: QuoteItem): string | null {
  if (!item.optionPart) return null;
  return `옵션 ${item.optionPart}` + (item.sparePart ? `  ·  스페어 ${item.sparePart}` : '');
}

/** 대체 줄 — 요청 파트 ≠ 공급 파트일 때의 이력(isSubstituted 와 짝). 우리 줄 — 고객에겐 안 실린다.
 *  값 예시: '↳  요청  P-100 · New  →  공급  P-200' · 대체 아니면 null */
export function substitutionLineText(item: QuoteItem): string | null {
  if (!isSubstituted(item)) return null;
  return `↳  요청  ${item.requestedPart}`
    + (conditionText(item.condition) ? ` · ${conditionText(item.condition)}` : '')
    + `  →  공급  ${item.optionPart}`;
}

// ── 파트 임대 견적서 품목표 열 명세 (v3 '파트임대' r20 헤더 실측 · 컨디션 없음, 임대 5열) ──
//    값 열이 판매와 다르다: 판매 {컨디션 AC·판매단가 AJ·판매금액 AN} → 임대 {기간 Y·임대단가 AD·월임대 AI·금액합계 AM}.
//    P1(렌더)은 임대 열을 파생(읽기 전용)으로 — 임대단가·기간 편집은 후속(P2).
export const RENTAL_PART_COLUMN_SPECS: readonly ColumnSpec[] = [
  { id: 'no',        title: 'No',           xlsxCol: ['C',  'C'],  align: 'left',   edit: 'derived', value: (_item, _ctx, index) => String(index + 1).padStart(2, '0') },
  { id: 'category',  title: '구분',           xlsxCol: ['D',  'H'],  align: 'left',   edit: 'picked',  field: 'category' },
  { id: 'part',      title: '품명 / 파트넘버', xlsxCol: ['I',  'X'],  align: 'left',   edit: 'text', editField: 'description', value: (item) => item.description ?? item.optionPart },
  { id: 'term',      title: '기간(개월)',      xlsxCol: ['Y',  'AA'], align: 'right',  edit: 'derived', field: 'rentalTermMonths' },
  { id: 'qty',       title: '수량',           xlsxCol: ['AB', 'AC'], align: 'right',  edit: 'derived', field: 'quantity' },
  { id: 'rentUnit',  title: '임대 단가',       xlsxCol: ['AD', 'AH'], align: 'right', numFmt: '#,##0', edit: 'derived', value: getRentalUnitKrw },
  { id: 'monthly',   title: '월 임대 금액',    xlsxCol: ['AI', 'AL'], align: 'right', numFmt: '#,##0', edit: 'derived', value: getRentalMonthlyKrw },
  { id: 'lineTotal', title: '금액 합계',       xlsxCol: ['AM', 'AP'], align: 'right', numFmt: '#,##0', edit: 'derived', value: getRentalTotalKrw },
] as const;

// ── 서버 임대 견적서 품목표 열 명세 (v3 '서버임대' r31 헤더 실측 · 구분 선두, No 없음) ──
export const RENTAL_SERVER_COLUMN_SPECS: readonly ColumnSpec[] = [
  { id: 'category',  title: '구분',           xlsxCol: ['D',  'H'],  align: 'left',   edit: 'picked',  field: 'category' },
  { id: 'part',      title: '품명 / 파트넘버', xlsxCol: ['I',  'X'],  align: 'left',   edit: 'text', editField: 'description', value: (item) => item.description ?? item.optionPart },
  { id: 'term',      title: '기간(개월)',      xlsxCol: ['Y',  'AA'], align: 'right',  edit: 'derived', field: 'rentalTermMonths' },
  { id: 'qty',       title: '수량',           xlsxCol: ['AB', 'AC'], align: 'right',  edit: 'derived', field: 'quantity' },
  { id: 'rentUnit',  title: '임대 단가',       xlsxCol: ['AD', 'AH'], align: 'right', numFmt: '#,##0', edit: 'derived', value: getRentalUnitKrw },
  { id: 'monthly',   title: '월 임대 금액',    xlsxCol: ['AI', 'AL'], align: 'right', numFmt: '#,##0', edit: 'derived', value: getRentalMonthlyKrw },
  { id: 'lineTotal', title: '금액 합계',       xlsxCol: ['AM', 'AP'], align: 'right', numFmt: '#,##0', edit: 'derived', value: getRentalTotalKrw },
] as const;

export function columnSpecs(doc: QuoteDocType): readonly ColumnSpec[] {
  switch (doc) {
    case 'server':        return SERVER_COLUMN_SPECS;
    case 'part':          return PART_COLUMN_SPECS;
    case 'part-rental':   return RENTAL_PART_COLUMN_SPECS;
    case 'server-rental': return RENTAL_SERVER_COLUMN_SPECS;
  }
}
